import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Divider, LinearProgress, Typography, useTheme } from '@mui/material';
import { emailNotificationSkynetApi } from 'app/services/api';
import clsx from 'clsx';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { compose } from 'redux';

import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useForm from 'hooks/useForm';
import { getDefaultErrorMessage } from 'utils/axios';
import { blobToBase64 } from 'utils/file';

import { selectUserData } from 'store/auth/selectors';
import { selectEmailSignatureTemplate } from 'store/companies/selectors';
import { closeDialogAction, showErrorMessageAction, showMessageAction } from 'store/global/actions';
import { getAllResourcesAction, getEmailActivities } from 'store/loanApplications/actions';

import { DisallowedEmailFileTypes, DisallowedFileSymbolsRegexp } from '../Activities.constants';

import { NewEmailBody, NewEmailFields, NewEmailFooter } from './components';
import { getSchema, MAX_ATTACHMENTS_SIZE } from './EmailCreationModal.config';
import { REDIRECT_DELAY_MS, SEND_EMAIL_DEBOUNCE_MS } from './EmailCreationModal.constants';
import { getStyles } from './EmailCreationModal.styles';

const getFileExtensionFromName = fileName => _.last(fileName.split('.'));

const emailBodyStyleOverride = `
  blockquote {
    border-left: 5px solid #ccc;
    padding: 0 21px;
    margin-left: 0;
    font-style: italic;
  }
  p {
    margin-top: 0px;
    margin-bottom: 0px;
  }
`;

const defaultForwardedLinkedResources = [];
const defaultObject = {};

const EmailCreationModal = ({
  formRef,
  isLoading,
  setIsLoading,
  applicationId,
  defaultValues = defaultObject,
  inReplyTo = null,
  isForward,
  repliedMessage,
  forwardedAttachmentNames,
  forwardedLinkedResources = defaultForwardedLinkedResources,
  dialogRef,
  triggerScrollIntoView,
}) => {
  const [showDropZone, setShowDropZone] = useState(false);
  const [hasRecipientFieldErrors, setHasRecipientFieldErrors] = useState(false);

  const theme = useTheme();

  const classes = getStyles(theme);
  const dispatch = useDispatch();

  const emailSignatureTemplate = useSelector(selectEmailSignatureTemplate);
  const senderData = useSelector(selectUserData);

  const fileInputRef = useRef(null);
  const dragOverTimeout = useRef(null);

  const [files, setFiles] = useState([]);
  const linkedResourcesRef = useRef([]);

  useEffectSafeStrictMode(() => {
    setIsLoading(true);

    dispatch(
      getAllResourcesAction(
        forwardedAttachmentNames,
        forwardedLinkedResources.map(({ resourceId }) => resourceId),
        inReplyTo,
      ),
    )
      .then(({ payload: { attachments, linkedResources } }) => {
        if (attachments?.length) {
          setFiles(attachments);
        }

        if (linkedResources?.length) {
          linkedResourcesRef.current = linkedResources;
        }
      })
      .finally(() => setIsLoading(false))
      .catch(err => {
        dispatch(showErrorMessageAction({ message: getDefaultErrorMessage(err), err }));
      });
  }, [
    applicationId,
    dispatch,
    forwardedAttachmentNames,
    inReplyTo,
    setIsLoading,
    forwardedLinkedResources,
  ]);

  const {
    form,
    isFormChanged,
    handleChange,
    validationErrors,
    setSchema,
    validateForm,
    validateField,
    setInForm,
  } = useForm({
    recipients: [],
    cc: [],
    bcc: [],
    subject: '',
    body: '',
    ...defaultValues,
  });

  useEffect(() => {
    const schema = getSchema(isForward);
    setSchema(schema);
  }, [isForward, setSchema]);

  const onSubmit = useMemo(
    () =>
      _.throttle(async () => {
        if (hasRecipientFieldErrors) {
          return;
        }

        if (!(await validateForm({ noFormChangedValidate: true }))) {
          return;
        }

        const rawBodyWithReply = form.body.concat(repliedMessage || '');

        const juice = await import('juice');

        const bodyWithReply = juice.default.inlineContent(rawBodyWithReply, emailBodyStyleOverride);
        const body = emailSignatureTemplate
          ? bodyWithReply.concat(emailSignatureTemplate)
          : bodyWithReply;

        const wrappedBody = `<div style="padding-left: 1.2rem; padding-top: 0.3rem;">${body}</div>`;

        const wrappedBodySize = new TextEncoder().encode(wrappedBody).length;
        const totalFilesSize = _.sumBy(files, 'size');
        const emailSize = totalFilesSize + wrappedBodySize;

        const isTotalFilesSizeExceedMax = emailSize > MAX_ATTACHMENTS_SIZE;

        if (isTotalFilesSizeExceedMax) {
          dispatch(
            showMessageAction({
              message: 'Total email and attachment size is over 20mb limit',
              variant: 'error',
            }),
          );

          return;
        }

        const ccData = form.cc.map(({ value }) => value);
        const bccData = form.bcc.map(({ value }) => value);
        const toData = form.recipients.map(({ value }) => value);

        const attachments = files.map(fileData => {
          return {
            resourceName: fileData.name,
            contentDisposition: 'attachment',
            resourceContent: fileData.base64String,
          };
        });

        const linkedResources = linkedResourcesRef.current.map((base64Resource, index) => {
          return {
            resourceName: forwardedLinkedResources?.[index]?.fileName,
            contentId: forwardedLinkedResources?.[index]?.resourceId,
            contentDisposition: 'inline',
            resourceContent: base64Resource.base64String,
          };
        });

        const payload = {
          body: wrappedBody,
          from: senderData.email,
          subject: form.subject,
          inReplyTo,
          isForward,
          cc: ccData,
          bcc: bccData,
          to: toData,
          emailResources: attachments.concat(linkedResources),
        };

        setIsLoading(true);

        emailNotificationSkynetApi
          .sendEmailWithFile({
            applicationId,
            payload,
          })
          .then(() => {
            dispatch(
              showMessageAction({
                message: 'Email sent successfully',
                variant: 'success',
              }),
            );
            dispatch(closeDialogAction());
          })
          .then(() => {
            triggerScrollIntoView();
            dispatch(getEmailActivities({ applicationId }));
          })
          .catch(err => {
            dispatch(showErrorMessageAction({ message: getDefaultErrorMessage(err), err }));
          })
          .finally(() => setIsLoading(false));
      }, SEND_EMAIL_DEBOUNCE_MS),
    [
      hasRecipientFieldErrors,
      validateForm,
      setIsLoading,
      applicationId,
      form,
      senderData,
      dispatch,
      files,
      emailSignatureTemplate,
      inReplyTo,
      isForward,
      repliedMessage,
      forwardedLinkedResources,
      triggerScrollIntoView,
    ],
  );

  // eslint-disable-next-line react-perf/jsx-no-new-function-as-prop
  const onSubmitHandler = e => {
    e.preventDefault();
    onSubmit();
  };

  const onAttachClick = useCallback(e => {
    e.preventDefault();

    fileInputRef?.current?.click();
  }, []);

  const attachFiles = useCallback(
    files => {
      const validatedFiles = [...files].filter(
        file =>
          !DisallowedEmailFileTypes.includes(getFileExtensionFromName(file.name)) &&
          !DisallowedFileSymbolsRegexp.test(file.name),
      );

      if (files.length > validatedFiles.length) {
        dispatch(
          showMessageAction({
            message: 'One or more files are disallowed to load',
            variant: 'error',
          }),
        );
      }

      // attached files converted to base64 to be sent to BE
      Promise.all(validatedFiles.map(file => blobToBase64(file))).then(base64Files => {
        setFiles(prevFiles => {
          const base64WithNames = base64Files.map((file, index) => ({
            name: validatedFiles[index].name,
            base64String: file,
            size: validatedFiles[index].size,
          }));
          const allFiles = prevFiles.concat(...base64WithNames);

          const uniqueFilesArray = _.uniqBy(allFiles, 'name');

          if (uniqueFilesArray.length < allFiles.length) {
            dispatch(
              showMessageAction({
                message: 'File with the same name is already uploaded',
                variant: 'error',
              }),
            );
          }

          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }

          return uniqueFilesArray;
        });
      });
    },
    [dispatch],
  );

  const onFileInputChange = useCallback(e => attachFiles(e.target.files), [attachFiles]);

  const onDragOver = useCallback(e => e.preventDefault(), []);

  const onDrop = useCallback(
    e => {
      e.preventDefault();

      setShowDropZone(false);

      attachFiles(e.dataTransfer.files);
    },
    [attachFiles],
  );

  const onDialogDragEnter = useCallback(e => {
    if (e.dataTransfer.types.includes('Files')) {
      setShowDropZone(true);
    }
  }, []);

  const onDialogDragLeave = useCallback(e => {
    if (!e.relatedTarget) {
      setShowDropZone(false);
    }
  }, []);

  const onDialogDragOver = useCallback(() => {
    clearTimeout(dragOverTimeout.current);

    dragOverTimeout.current = setTimeout(() => {
      setShowDropZone(false);
    }, REDIRECT_DELAY_MS);
  }, [dragOverTimeout]);

  useEffect(() => {
    if (!dialogRef || !dialogRef.current) {
      return;
    }

    dialogRef.current.addEventListener('dragenter', onDialogDragEnter);
    dialogRef.current.addEventListener('dragleave', onDialogDragLeave);
    dialogRef.current.addEventListener('dragover', onDialogDragOver);
  }, [onDialogDragEnter, onDialogDragLeave, onDialogDragOver, dialogRef]);

  return (
    <Box
      noValidate
      className="flex flex-col relative"
      component="form"
      data-ischanged={isFormChanged}
      ref={formRef}
      sx={classes.modalContainer}
      onSubmit={onSubmitHandler}
    >
      <LinearProgress className={isLoading ? 'visible' : 'invisible'} color="primary" />

      <NewEmailFields
        form={form}
        isLoading={isLoading}
        setInForm={setInForm}
        shouldExpandCc={!!defaultValues.cc?.length}
        validateField={validateField}
        validationErrors={validationErrors}
        onChange={handleChange}
        onRecipientFieldErrorsChange={setHasRecipientFieldErrors}
      />

      <NewEmailBody
        applicationId={applicationId}
        files={files}
        form={form}
        forwardedLinkedResources={forwardedLinkedResources}
        inReplyTo={inReplyTo}
        isLoading={isLoading}
        repliedMessage={repliedMessage}
        setFiles={setFiles}
        setInForm={setInForm}
        setIsLoading={setIsLoading}
        validateField={validateField}
        validationErrors={validationErrors}
      />

      {showDropZone && (
        <div
          className={clsx(
            classes.dropZone,
            'absolute w-full h-full flex items-center justify-center opacity-50',
          )}
          onDragOver={onDragOver}
          onDrop={onDrop}
        >
          <Typography variant="bold32">Drop files here</Typography>
        </div>
      )}

      <input
        multiple
        className="hidden"
        ref={fileInputRef}
        type="file"
        onChange={onFileInputChange}
      />

      <Divider />

      <NewEmailFooter
        filesAmount={files.length}
        isLoading={isLoading}
        onAttachFile={onAttachClick}
      />
    </Box>
  );
};

EmailCreationModal.propTypes = {
  ...WithLoadingPropTypes,
  applicationId: PropTypes.string.isRequired,
  formRef: PropTypes.shape({ current: PropTypes.object }).isRequired,
  dialogRef: PropTypes.shape({ current: PropTypes.object }).isRequired,
  defaultValues: PropTypes.object,
  inReplyTo: PropTypes.string,
  forwardedLinkedResources: PropTypes.array,
};

export default compose(withLoading, memo)(EmailCreationModal);
